import {graphql, useFragment, useLazyLoadQuery} from 'react-relay'
import {useParams} from 'react-router'

import {useAppSelector} from '../../../../hooks/react-redux'
import type {QueryParam, SetQueryParam} from '../../../../hooks/routes'
import {useQueryParam, useQueryParamState} from '../../../../hooks/routes'
import {getPipeline} from '../../../../selectors'
import type {PipelineId, ProjectInternalId} from '../../../../types'
import {toPipelineId} from '../../../../types'
import {DEFAULT_NAME as DEFAULT_BRANCH_NAME} from '../../../../utils/branchNames'
import {isPipelinesBranchedYamlSettingsEnabled} from '../../../../utils/featureToggles'

import type {pipelineHeadBuildTypeFragment$key} from './__generated__/pipelineHeadBuildTypeFragment.graphql'
import type {pipelineInternalIdQuery} from './__generated__/pipelineInternalIdQuery.graphql'
import type {pipelineParentProjectFragment$key} from './__generated__/pipelineParentProjectFragment.graphql'

export const usePipelineId = (): PipelineId => {
  const {pipelineId = ''} = useParams()
  return toPipelineId(pipelineId)
}

export function usePipelineScope() {
  const pipelineId = usePipelineId()
  const branch = useQueryParam('branch')
  return {
    pipelineId,
    branch: isPipelinesBranchedYamlSettingsEnabled && branch ? branch : DEFAULT_BRANCH_NAME,
  }
}

const PipelineInternalIdQuery = graphql`
  query pipelineInternalIdQuery($locator: String!, $skip: Boolean!) {
    pipeline(pipelineLocator: $locator) @skip(if: $skip) {
      internalId
    }
  }
`

export function usePipelineInternalId(
  pipelineId: PipelineId | null | undefined,
  pipelineInternalId?: ProjectInternalId | null | undefined,
): ProjectInternalId | null | undefined {
  const internalIdFromRedux = useAppSelector(
    state => pipelineInternalId ?? getPipeline(state, pipelineId)?.internalId,
  )
  const {pipeline} = useLazyLoadQuery<pipelineInternalIdQuery>(PipelineInternalIdQuery, {
    locator: `id:${pipelineId}`,
    skip: pipelineId == null || internalIdFromRedux != null,
  })
  return internalIdFromRedux ?? pipeline?.internalId
}

export const useJobIdState = (): [QueryParam, SetQueryParam] => useQueryParamState('job')

const PipelineHeadFragment = graphql`
  fragment pipelineHeadBuildTypeFragment on BuildTypeRest {
    id
  }
`

export const usePipelineHeadId = (key: pipelineHeadBuildTypeFragment$key | null) => {
  const {id} = useFragment(PipelineHeadFragment, key) || {}
  return id
}

const PipelineParentProjectFragment = graphql`
  fragment pipelineParentProjectFragment on ProjectRest {
    id
    internalId
  }
`

const usePipelineParentProject = (key: pipelineParentProjectFragment$key | null) =>
  useFragment(PipelineParentProjectFragment, key)

export const usePipelineParentProjectId = (key: pipelineParentProjectFragment$key | null) => {
  const {id} = usePipelineParentProject(key) || {}
  return id
}
